import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  Container, 
  Typography, 
  Paper, 
  TextField, 
  AppBar, 
  Toolbar, 
  Fade,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip
} from '@mui/material';
import { 
  Business as BusinessIcon,
  Build as BuildIcon,
  VerifiedUser as VerifiedUserIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Engineering as EngineeringIcon
} from '@mui/icons-material';
import cpengBadge from '../assets/cpeng-badge.png';
import { useNavigate } from 'react-router-dom';
import logo from '../assets/gemistruct-logo.png';
import wordmark from '../assets/gemistruct-wordmark.png';
import heroBg from '../assets/sydney-harbour.jpg';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

export default function LandingPage() {
  const navigate = useNavigate();
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactStatus, setContactStatus] = useState('');
  const [contactError, setContactError] = useState('');

  // No initialization needed for Firebase

  async function handleContactSubmit(e) {
    e.preventDefault();
    setContactStatus('');
    setContactError('');
    
    console.log('Attempting to submit contact form...');
    console.log('Firebase db object:', db);
    
    try {
      // Save message to Firebase Firestore
      const docRef = await addDoc(collection(db, 'contactMessages'), {
        name: contactName,
        email: contactEmail,
        message: contactMessage,
        timestamp: serverTimestamp(),
        status: 'new'
      });
      
      console.log('Message saved successfully with ID:', docRef.id);
      setContactStatus('Message sent successfully! We\'ll get back to you within 24 hours.');
      setContactName('');
      setContactEmail('');
      setContactMessage('');
    } catch (err) {
      console.error('Firebase Error Details:', err);
      console.error('Error Code:', err.code);
      console.error('Error Message:', err.message);
      setContactError(`Failed to send message: ${err.message}. Please try again or contact us directly.`);
    }
  }

  return (
    <>
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          zIndex: 10,
        }} 
        elevation={0}
      >
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img src={logo} alt="Gemistruct Logo" style={{ height: 48, marginRight: 16 }} />
            <img src={wordmark} alt="Gemistruct" style={{ height: 36 }} />
          </Box>
          
          {/* Navigation Links */}
          <Box sx={{ display: 'flex', gap: 2, mr: 3 }}>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/consulting')}
            >
              CONSULTING SERVICES
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'secondary.main',
                  color: 'white',
                }
              }}
              onClick={() => navigate('/engineering-tools')}
            >
              ENGINEERING TOOLS
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#about');
                setTimeout(() => {
                  document.getElementById('about').scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              ABOUT US
            </Button>
            <Button 
              sx={{ 
                color: 'text.primary',
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                }
              }}
              onClick={() => {
                navigate('/#contact');
                setTimeout(() => {
                  document.getElementById('contact').scrollIntoView({ 
                    behavior: 'smooth',
                    block: 'start'
                  });
                }, 100);
              }}
            >
              CONTACT
            </Button>
          </Box>
          
          <Button 
            color="primary" 
            variant="contained" 
            sx={{ 
              fontWeight: 600,
              '&:hover': {
                transform: 'scale(1.05)',
                transition: 'transform 0.2s ease-in-out',
              }
            }}
            onClick={() => navigate('/signin')}
          >
            Sign In
          </Button>
        </Toolbar>
      </AppBar>
      
      {/* Hero Section */}
      <Box
        sx={{
          position: 'relative',
          minHeight: { xs: 500, md: 600 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundImage: `url(${heroBg})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            bgcolor: 'rgba(20, 40, 80, 0.7)',
            zIndex: 1,
          }}
        />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2, color: '#fff', textAlign: 'center' }}>
          <Fade in timeout={1200}>
            <Box>
              <Typography variant="h2" fontWeight={700} gutterBottom color="#fff" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
                Expert Structural Engineering Services
              </Typography>
              <Typography variant="h5" color="#f5f5f5" paragraph sx={{ mb: 4, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                Professional consulting and cloud-based engineering tools for Australian structural engineering industry
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button 
                  variant="contained" 
                  size="large" 
                  color="primary" 
                  startIcon={<BusinessIcon />}
                  sx={{ 
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    padding: '16px 32px',
                    borderRadius: 2,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    minWidth: 200,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s ease-in-out',
                    }
                  }} 
                  onClick={() => navigate('/consulting')}
                >
                  Consulting Services
                </Button>
                <Button 
                  variant="contained" 
                  size="large" 
                  color="secondary" 
                  startIcon={<BuildIcon />}
                  sx={{ 
                    fontSize: '1.25rem',
                    fontWeight: 700,
                    padding: '16px 32px',
                    borderRadius: 2,
                    textTransform: 'uppercase',
                    letterSpacing: 1,
                    minWidth: 200,
                    '&:hover': {
                      transform: 'scale(1.05)',
                      transition: 'transform 0.2s ease-in-out',
                    }
                  }} 
                  onClick={() => navigate('/engineering-tools')}
                >
                  Engineering Tools
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Services Section - Two Columns */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6, letterSpacing: 0 }}>
          Our Services
        </Typography>
        <Grid container spacing={6}>
          {/* Consulting Services Column */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 56, height: 56, mr: 2 }}>
                    <BusinessIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    Consulting Services
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  Professional structural engineering consulting for industrial and commercial projects.
                  Backed by CPEng, RPEQ, and NER certifications with full design insurance, we deliver safe, efficient, and compliant solutions across oil & gas, mining, energy, water infrastructure, and warehousing, from concept design to construction documentation.

                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="primary.main">
                    What We Offer:
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" sx={{ mb: 1 }}>Structural analysis and design</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Construction documentation</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Expert witness services</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Specialized Engineering</Typography>
                  </Box>
                </Box>
                <Button 
                  variant="contained" 
                  fullWidth 
                  size="large" 
                  sx={{ mt: 4 }}
                  onClick={() => navigate('/consulting')}
                >
                  Learn More About Consulting
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Engineering Tools Column */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'secondary.main', width: 56, height: 56, mr: 2 }}>
                    <BuildIcon sx={{ fontSize: 32 }} />
                  </Avatar>
                  <Typography variant="h4" fontWeight={700} color="secondary.main">
                    Engineering Tools
                  </Typography>
                </Box>
                <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8 }}>
                  Cloud-based structural engineering tools purpose-built for compliance with Australian Standards. 
                  Designed to support practicing engineers, these tools help streamline design workflows, improve accuracy, and save time on everyday calculations.
                </Typography>
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" fontWeight={600} gutterBottom color="secondary.main">
                    Available Tools (Coming Soon):
                  </Typography>
                  <Box component="ul" sx={{ pl: 2 }}>
                    <Typography component="li" sx={{ mb: 1 }}>Steel beam and column design</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Concrete member design</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Foundation design (shallow and deep foundations)</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Steel connection design</Typography>
                    <Typography component="li" sx={{ mb: 1 }}>Section library and database</Typography>
                  </Box>
                </Box>
                <Button 
                  variant="contained" 
                  fullWidth 
                  size="large" 
                  color="secondary"
                  sx={{ mt: 4, borderWidth: 2 }}
                  onClick={() => navigate('/engineering-tools')}
                >
                  Explore Engineering Tools
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* About us Section */}
      <Box id="about" sx={{ bgcolor: 'grey.100', py: 10 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 8, letterSpacing: 1 }}>
            About Us
          </Typography>
          
          <Card elevation={4} sx={{ borderRadius: 4, overflow: 'hidden' }}>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #1976d2 0%, #2e7d32 100%)',
              p: 5, 
              color: 'white',
              textAlign: 'center'
            }}>
              <Typography variant="h4" fontWeight={700} gutterBottom>
                Specialised Structural Engineering Excellence
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                Delivering Safe, Efficient & Practical Solutions Across Australia
              </Typography>
            </Box>
            
            <CardContent sx={{ p: 6 }}>
              <Grid container spacing={6} alignItems="center">
                {/* Left Column - Company Description */}
                <Grid item xs={12} md={7}>
                  <Typography variant="h6" paragraph sx={{ fontSize: '1.25rem', lineHeight: 1.8, color: 'text.primary', mb: 4, fontWeight: 600 }}>
                    Gemistruct was established to provide specialised structural engineering services and practical design solutions tailored to the needs of Australian industries. Our purpose is to deliver safe, efficient, and constructible outcomes for clients across a wide range of projects.
                  </Typography>
                  
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
                    Our founder is a Chartered Professional Engineer (CPEng), Registered Professional Engineer of Queensland (RPEQ), and listed on the National Engineering Register (NER). He is also a proud member of the Australian Steel Institute. With more than 20 years of consulting experience, he has delivered structural design for buildings, energy infrastructure, water assets, oil & gas facilities, warehouses, and commercial and industrial projects.
                  </Typography>
                  
                  <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.8, mb: 4 }}>
                    Supporting him is a highly skilled design and drafting team, each with more than 20 years of experience in steel and concrete structures, foundations, and construction documentation. Together, we bring both technical depth and drafting precision to projects of all sizes — from major industrial facilities to smaller, specialised works.
                  </Typography>
                  
                          </Grid>
                
                {/* Right Column - Founder & Qualifications */}
                <Grid item xs={12} md={5}>
                  <Box sx={{ textAlign: 'center' }}>

                    {/* CPEng Badge */}
                    <Box sx={{ 
                      width: 160, 
                      height: 160, 
                      mx: 'auto', 
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <img 
                        src={cpengBadge} 
                        alt="CPEng Badge - Chartered Professional Engineer" 
                        style={{ 
                          width: '100%', 
                          height: '100%', 
                          objectFit: 'contain',
                          filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.2))'
                        }}
                      />
                    </Box>
                    
                    {/* Founder Title */}
                    <Typography variant="h6" gutterBottom fontWeight={700} color="primary.main" sx={{ mb: 3 }}>
                      Chartered Professional Engineer
                    </Typography>
                    
                    {/* Experience Badge */}
                    <Box sx={{ 
                      bgcolor: 'primary.main', 
                      color: 'white', 
                      py: 1, 
                      px: 3, 
                      borderRadius: 3, 
                      display: 'inline-block',
                      mb: 3,
                      fontWeight: 600
                    }}>
                      20 Years Experience
                    </Box>
                    
                    {/* Qualifications Grid */}
                    <Grid container spacing={2} justifyContent="center" sx={{ mb: 3 }}>
                      <Grid item>
                        <Chip 
                          label="CPEng" 
                          size="medium" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ fontWeight: 600, borderWidth: 2 }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip 
                          label="RPEQ" 
                          size="medium" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ fontWeight: 600, borderWidth: 2 }}
                        />
                      </Grid>
                      <Grid item>
                        <Chip 
                          label="NER" 
                          size="medium" 
                          color="primary" 
                          variant="outlined" 
                          sx={{ fontWeight: 600, borderWidth: 2 }}
                        />
                      </Grid>
                    </Grid>
                    
                    {/* Founder Description */}
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      Our founder's career spans structural design for buildings, energy infrastructure, water assets, oil & gas facilities, warehouses, and commercial and industrial projects.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Contact Section */}
      <Container id="contact" maxWidth="md" sx={{ py: 8 }}>
        <Typography variant="h3" align="center" gutterBottom fontWeight={700} sx={{ mb: 6 }}>
          Get In Touch
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3, textAlign: 'center' }}>
              <CardContent sx={{ p: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                  <EmailIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Email Us
                </Typography>
                                 <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                   Send us a message and we'll get back to you within 24 hours.
                 </Typography>
                 <Typography 
                   component="a" 
                   href="mailto:info@gemistruct.com"
                   variant="body1" 
                   color="primary.main" 
                   fontWeight={600}
                   sx={{ 
                     textDecoration: 'none',
                     '&:hover': {
                       textDecoration: 'underline',
                       cursor: 'pointer'
                     }
                   }}
                 >
                   info@gemistruct.com
                 </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3, textAlign: 'center' }}>
              <CardContent sx={{ p: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                  <PhoneIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Call Us
                </Typography>
                                 <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                   Available during business hours for urgent inquiries and consultations.
                 </Typography>
                 <Typography 
                   component="a" 
                   href="tel:0413766585"
                   variant="body1" 
                   color="primary.main" 
                   fontWeight={600}
                   sx={{ 
                     textDecoration: 'none',
                     '&:hover': {
                       textDecoration: 'underline',
                       cursor: 'pointer'
                     }
                   }}
                 >
                   0413 766 585
                 </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card elevation={3} sx={{ height: '100%', borderRadius: 3, textAlign: 'center' }}>
              <CardContent sx={{ p: 4 }}>
                <Avatar sx={{ bgcolor: 'primary.main', width: 64, height: 64, mx: 'auto', mb: 2 }}>
                  <LocationIcon sx={{ fontSize: 32 }} />
                </Avatar>
                <Typography variant="h6" gutterBottom fontWeight={600}>
                  Service Areas
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Serving Queensland and Australia-wide with remote consulting and on-site services.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mt: 6 }}>
          <Typography variant="h5" align="center" gutterBottom>Send Us a Message</Typography>
          <Box component="form" sx={{ mt: 3 }} onSubmit={handleContactSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Your Name" 
                  fullWidth 
                  value={contactName} 
                  onChange={e => setContactName(e.target.value)} 
                  required 
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Your Email" 
                  fullWidth 
                  value={contactEmail} 
                  onChange={e => setContactEmail(e.target.value)} 
                  required 
                  type="email" 
                />
              </Grid>
              <Grid item xs={12}>
                <TextField 
                  label="Message" 
                  fullWidth 
                  multiline 
                  rows={4} 
                  value={contactMessage} 
                  onChange={e => setContactMessage(e.target.value)} 
                  required 
                />
              </Grid>
              <Grid item xs={12}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  fullWidth 
                  size="large" 
                  type="submit"
                  sx={{ py: 1.5 }}
                >
                  Send Message
                </Button>
              </Grid>
            </Grid>
            {contactStatus && (
              <Typography color="success.main" align="center" sx={{ mt: 2 }}>
                {contactStatus}
              </Typography>
            )}
            {contactError && (
              <Typography color="error.main" align="center" sx={{ mt: 2 }}>
                {contactError}
              </Typography>
            )}
          </Box>
        </Paper>
      </Container>

        {/* Footer */}
        <Box sx={{ bgcolor: 'grey.900', color: 'grey.100', py: 4 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  {/* Company name in text instead of logo */}
                  <Typography variant="h5" fontWeight={700} sx={{ mb: 1, color: 'white' }}>
                    Gemistruct
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2, color: 'grey.300' }}>
                    Professional consulting and cloud-based engineering tools for Australian structural engineering industry.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                     <Chip label="CPEng" size="small" sx={{ 
                       color: 'white', 
                       borderColor: 'white', 
                       '& .MuiChip-label': { color: 'white' }
                     }} variant="outlined" />
                     <Chip label="RPEQ" size="small" sx={{ 
                       color: 'white', 
                       borderColor: 'white', 
                       '& .MuiChip-label': { color: 'white' }
                     }} variant="outlined" />
                     <Chip label="NER" size="small" sx={{ 
                       color: 'white', 
                       borderColor: 'white', 
                       '& .MuiChip-label': { color: 'white' }
                     }} variant="outlined" />
                   </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Quick Links</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Button 
                    color="inherit" 
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => navigate('/consulting')}
                  >
                    Consulting Services
                  </Button>
                  <Button 
                    color="inherit" 
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => navigate('/engineering-tools')}
                  >
                    Engineering Tools
                  </Button>
                  <Button 
                    color="inherit" 
                    sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                    onClick={() => navigate('/signin')}
                  >
                    Client Portal
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Box sx={{ borderTop: 1, borderColor: 'grey.800', pt: 3, mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'grey.100' }}>
                Copyright © {new Date().getFullYear()} GEMISTRUCT PTY LTD ABN: 80 684 710 594{' '}
                <Button 
                  color="inherit" 
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.875rem',
                    color: 'grey.100',
                    '&:hover': { color: 'white' },
                    p: 0,
                    minWidth: 'auto'
                  }}
                  onClick={() => navigate('/privacy')}
                >
                  Privacy
                </Button>
                {' '}|{' '}
                <Button 
                  color="inherit" 
                  sx={{ 
                    textTransform: 'none', 
                    fontSize: '0.875rem',
                    color: 'grey.100',
                    '&:hover': { color: 'white' },
                    p: 0,
                    minWidth: 'auto'
                  }}
                  onClick={() => navigate('/terms')}
                >
                  Terms & Conditions
                </Button>
              </Typography>
            </Box>
          </Container>
        </Box>
    </>
  );
} 